<template>
    <div class="product-update">
        <b-form @submit="save" novalidate>
            <b-container>
                <h1 class="mb-4">Edit Single Product</h1>
                <h3 class="mb-4">{{product.code}}</h3>
                <b-tabs>
                    <b-tab title="Main">
                        <h3 class="mb-4">{{product.name}}</h3>

                        <b-form-group>
                            <b-row>
                                <b-col cols="6">
                                    <label for="product-variation">Product Variation</label>
                                    <b-form-input v-model="product.variation" v-on:change="product.code = generateProductCode(product)" type="text" id="product-variation" placeholder="e.g. 2800, 3400, 4000"></b-form-input>
                                </b-col>

                                <b-col cols="6">
                                    <label for="product-range">Product Range</label>
                                    <b-form-select v-model="product.range" :options="getProductTypes()"></b-form-select>
                                </b-col>
                            </b-row>
                        </b-form-group>

                        <b-form-group>
                            <b-row>
                                <b-col cols="4">
                                    <label for="product-code">Product Code (automatically generated)</label>
                                    <b-form-input v-model="product.code" type="text" id="product-code" readonly></b-form-input>
                                </b-col>

                                <b-col cols="4">
                                    <label for="product-ean">EAN Number</label>
                                    <b-form-input v-model="product.ean" type="text" id="product-ean"></b-form-input>
                                </b-col>

                                <b-col cols="4">
                                    <label for="product-sides">Number of sides</label>
                                    <b-form-input v-model="product.sides" type="number" id="product-sides"></b-form-input>
                                </b-col>
                            </b-row>
                        </b-form-group>

                        <hr />

                        <b-form-group>
                            <label for="product-description">Product Description</label>
                            <b-form-textarea v-model="product.description" :rows="3" id="product-description"></b-form-textarea>
                        </b-form-group>

                        <b-form-group>
                            <label for="product-specification">Product Specification</label>
                            <b-form-textarea v-model="product.specification" :rows="3" id="product-specification"></b-form-textarea>
                        </b-form-group>

                        <b-form-group>
                            <label for="product-guttering">Guttering</label>
                            <b-form-input v-model="product.guttering" type="text" id="product-guttering"></b-form-input>
                        </b-form-group>
                    </b-tab>

                    <b-tab title="Product Family">
                        <p>Images are managed centrally via Product Families. Select the Product Family for this product below.</p>

                        <b-form-select v-model="product.family" :options="productFamilyLabels"></b-form-select>
                    </b-tab>

                    <b-tab title="Sizes">
                        <b-form-group>
                            <b-row>
                                <b-col cols="6">
                                    <label for="size-label">Size Label</label>
                                    <b-form-input v-model="product.sizeLabel" id="size-label"></b-form-input>
                                </b-col>
                            </b-row>
                        </b-form-group>

                        <hr />

                        <div class="row">
                            <div class="col-sm-6">
                                <h3>Height</h3>

                                <b-form-group>
                                    <b-row>
                                        <b-col cols="6">
                                            <label for="size-height-metric">Height (cm)</label>
                                            <b-form-input v-model="product.heightMetric" v-on:change="calculateDimensions('heightMetric', 'cm', 'heightImperial')" id="size-height-metric"></b-form-input>
                                        </b-col>

                                        <b-col cols="6">
                                            <label for="size-height-imperial">Height (inches)</label>
                                            <b-form-input v-model="product.heightImperial" v-on:change="calculateDimensions('heightImperial', 'in', 'heightMetric')" id="size-height-imperial"></b-form-input>
                                        </b-col>
                                    </b-row>
                                </b-form-group>
                            </div>

                            <div class="col-sm-6">
                                <h3>Width</h3>

                                <b-form-group>
                                    <b-row>
                                        <b-col cols="6">
                                            <label for="size-height-metric">Width (cm)</label>
                                            <b-form-input v-model="product.widthMetric" v-on:change="calculateDimensions('widthMetric', 'cm', 'widthImperial')" id="size-width-metric"></b-form-input>
                                        </b-col>

                                        <b-col cols="6">
                                            <label for="size-height-imperial">Width (inches)</label>
                                            <b-form-input v-model="product.widthImperial" v-on:change="calculateDimensions('widthImperial', 'in', 'widthMetric')" id="size-width-imperial"></b-form-input>
                                        </b-col>
                                    </b-row>
                                </b-form-group>
                            </div>
                        </div>

                        <div class="row mt-4">
                            <div class="col-sm-6">
                                <h3>Depth</h3>

                                <b-form-group>
                                    <b-row>
                                        <b-col cols="6">
                                            <label for="size-height-metric">Depth (cm)</label>
                                            <b-form-input v-model="product.depthMetric" v-on:change="calculateDimensions('depthMetric', 'cm', 'depthImperial')" id="size-depth-metric"></b-form-input>
                                        </b-col>

                                        <b-col cols="6">
                                            <label for="size-depth-imperial">Depth (inches)</label>
                                            <b-form-input v-model="product.depthImperial" v-on:change="calculateDimensions('depthImperial', 'in', 'depthMetric')" id="size-depth-imperial"></b-form-input>
                                        </b-col>
                                    </b-row>
                                </b-form-group>
                            </div>

                            <div class="col-sm-6">
                                <h3>Area</h3>

                                <b-form-group>
                                    <b-row>
                                        <b-col cols="6">
                                            <label for="size-height-metric">Area (cm)</label>
                                            <b-form-input v-model="product.areaMetric" v-on:change="calculateDimensions('areaMetric', 'cm', 'areaImperial')" id="size-area-metric"></b-form-input>
                                        </b-col>

                                        <b-col cols="6">
                                            <label for="size-height-imperial">Area (inches)</label>
                                            <b-form-input v-model="product.areaImperial" v-on:change="calculateDimensions('areaImperial', 'in', 'areaMetric')" id="size-area-imperial"></b-form-input>
                                        </b-col>
                                    </b-row>
                                </b-form-group>
                            </div>
                        </div>

                    </b-tab>

                    <b-tab title="Colours">
                        <b-form-group>
                            <b-row>
                                <b-col cols="6">
                                    <label for="colour">Frame Colour</label>
                                    <b-form-select v-model="product.colour" @change.native="product.code = ''" :options="getColourOptions()"></b-form-select>
                                </b-col>
                            </b-row>
                        </b-form-group>
                    </b-tab>

                    <b-tab title="Roofs">
                        <b-form-group>
                            <b-row>
                                <b-col cols="6">
                                    <label for="roof">Roof</label>
                                    <b-form-select v-model="product.roof" @change.native="product.code = ''" :options="getRoofOptions()"></b-form-select>
                                </b-col>
                            </b-row>
                        </b-form-group>
                    </b-tab>

                    <b-tab title="Pricing">
                        <b-form-group>
                            <b-row>
                                <b-col cols="6">
                                    <label for="cost">Trade Price</label>
                                    <b-input-group>
                                        <b-input-group-addon>&pound;</b-input-group-addon>
                                        <b-form-input v-model="product.costPrice" id="cost" type="number"></b-form-input>
                                    </b-input-group>
                                </b-col>

                                <b-col cols="6">
                                    <label for="ssp">Raw Product Price (exc. vat)</label>
                                    <b-input-group>
                                        <b-input-group-addon>&pound;</b-input-group-addon>
                                        <b-form-input v-model="product.ssp" id="ssp" type="number"></b-form-input>
                                    </b-input-group>
                                </b-col>
                            </b-row>

                            <hr />

                            <h4 class="mb-4">International Pricing</h4>

                            <div v-for="country in countries">
                                <b-form-group>
                                    <b-row>
                                        <b-col cols="3" class="text-right">
                                            {{ country.label }}
                                        </b-col>

                                        <b-col cols="6">
                                            <b-input-group>
                                                <b-form-input v-model="product.internationalPricing[country.label]"></b-form-input>
                                                <b-input-group-addon>{{ country.currency }}</b-input-group-addon>
                                            </b-input-group>
                                        </b-col>

                                        <b-col cols="3">
                                            <b-button size="sm" variant="secondary" v-on:click="calculatePrice(country.label)" v-if="country.label === 'UK'">
                                                Recalculate
                                            </b-button>
                                        </b-col>
                                    </b-row>
                                </b-form-group>
                            </div>
                        </b-form-group>
                    </b-tab>

                    <b-tab title="Optional Extras">
                        <p>Optional Extras are managed in the Optional Extras section.</p>
                    </b-tab>
                </b-tabs>
            </b-container>

            <b-navbar fixed="bottom">
                <b-container>
                    <b-nav>
                        <b-button size="lg" v-on:click="back" type="button" variant="back">Back</b-button>
                        <b-button size="lg" v-b-modal.duplicate-modal type="button" class="ml-1">Duplicate</b-button>
                    </b-nav>

                    <b-nav class="ml-auto">
                        <b-button size="lg" variant="link" class="mr-1 text-danger" v-on:click="del">Delete</b-button>
                        <b-button size="lg" type="submit" variant="primary" class="mr-1">Save</b-button>
                        <b-button v-if="product.published" v-on:click="unpublish" variant="success">Published</b-button>
                        <b-button v-if="!product.published" v-on:click="publish">Not Published</b-button>
                    </b-nav>
                </b-container>
            </b-navbar>
        </b-form>

        <simplert :useRadius="true" :useIcon="true" ref="simplert"></simplert>

        <b-modal ref="duplicateModal" id="duplicate-modal" title="Duplicate" size="lg" hide-footer>
            <p>Use the form below to generate new products.</p>

            <b-form @submit="duplicate" novalidate>
                <b-row>
                    <b-col cols="11">
                        <b-row>
                            <b-col>Frame Colour</b-col>
                            <b-col>Roof</b-col>
                            <b-col>EAN</b-col>
                            <b-col>Product Code</b-col>
                            <b-col>Trade Price</b-col>
                            <b-col>Raw Product Price</b-col>
                            <b-col>UK SSP</b-col>
                        </b-row>
                    </b-col>
                </b-row>

                <hr />

                <div v-for="(entry, index) in duplicateForm">
                    <b-form-group>
                        <b-row>
                            <b-col cols="11">
                                <b-row>
                                    <b-col>
                                        <b-form-select v-model="entry.colour" v-on:change="entry.code = generateProductCode(entry)" :options="getColourOptions()"></b-form-select>
                                    </b-col>

                                    <b-col>
                                        <b-form-select v-model="entry.roof" v-on:change="entry.code = generateProductCode(entry)" :options="getRoofOptions()"></b-form-select>
                                    </b-col>

                                    <b-col>
                                        <b-form-input v-model="entry.ean" v-on:change="entry.code = generateProductCode(entry)"></b-form-input>
                                    </b-col>

                                    <b-col>
                                        <b-form-input v-model="entry.code" readonly></b-form-input>
                                        <p v-if="entry._exists">This product code already exists!</p>
                                    </b-col>

                                    <b-col>
                                        <b-input-group>
                                            <b-input-group-addon>&pound;</b-input-group-addon>
                                            <b-form-input v-model="entry.costPrice" type="number"></b-form-input>
                                        </b-input-group>
                                    </b-col>

                                    <b-col>
                                        <b-input-group>
                                            <b-input-group-addon>&pound;</b-input-group-addon>
                                            <b-form-input v-model="entry.ssp" type="number"></b-form-input>
                                        </b-input-group>
                                    </b-col>

                                    <b-col>
                                        <b-input-group>
                                            <b-input-group-addon>&pound;</b-input-group-addon>
                                            <b-form-input v-model="entry.internationalPricing['UK']" type="number"></b-form-input>
                                        </b-input-group>
                                    </b-col>
                                </b-row>
                            </b-col>

                            <b-col cols="1">
                                <b-button size="sm" variant="danger" v-on:click="removeProductFromDuplicateForm(index)">&times;</b-button>
                            </b-col>
                        </b-row>
                    </b-form-group>
                </div>

                <b-button size="sm" v-on:click="addProductToDuplicateForm" v-show="!duplicating">Add Another</b-button>

                <div class="mt-4 text-center" v-show="duplicateForm.length > 0">
                    <div class="mb-4" v-show="duplicating">
                        <pulse-loader :loading="duplicating" color="#01a94e"></pulse-loader>
                    </div>

                    <div v-show="duplicating">
                        <b-progress :value="duplicatesCreated" :max="duplicateForm.length" show-progress></b-progress>
                    </div>

                    <b-button variant="warning" size="lg" v-on:click="createDuplicates" v-show="!duplicating">Create Duplicates</b-button>
                </div>
            </b-form>
        </b-modal>
    </div>
</template>

<script>
  import Simplert from 'vue2-simplert'
  import ProductImages from './ProductImages.vue'
  import ProductOptions from './ProductOptions.vue'
  import { PulseLoader } from 'vue-spinner/dist/vue-spinner'
  import ProductSearch from './ProductSearch'
  import {ServerValue} from 'firebase/database'
  // import Vue from 'vue'

  export default {
    name: 'productUpdate',
    props: ['fb'],
    components: {
      ProductOptions,
      ProductImages,
      Simplert,
      PulseLoader
    },
    data () {
      return {
        duplicateForm: [],
        duplicating: false,
        duplicatesCreated: 0,
        updatedFamily: false,
        saving: false
      }
    },
    mounted () {
      function updateDuplicateExists (index, exists) {
        this.duplicateForm[index]['_exists'] = exists
        this.$forceUpdate()
      }
      this.$watch('duplicateForm', () => {
        for (let i = 0; i < this.duplicateForm.length; i++) {
          this.duplicateForm[i].code = this.$productCode.generate(this.duplicateForm[i])
          // only check if the product exists if we haven't checked it already
          if (!this.duplicateForm[i].hasOwnProperty('_exists')) {
            this.productExists(this.duplicateForm[i].code).then(updateDuplicateExists.bind(this, i))
          }
        }
      }, {
        deep: true
      })
    },
    firebase () {
      return {
        product: {
          source: this.fb.database().ref('products/' + this.$route.params.id),
          asObject: true,
          readyCallback () {
            if (!this.product.internationalPricing) {
              this.product.internationalPricing = {}
            }
            if (!this.product.family) {
              this.product.family = ''
            }
            if (!this.product.variation) {
              this.product.variation = ''
            }

            this.updateFamily()

            this.$watch('product', (prop, propB) => {
              if (!this.saving) {
                console.log('product change detected')
                if (this.product._family['.key'] !== prop.family) {
                  this.updateFamily()
                }
                console.log('updating product code')
                this.product.code = this.$productCode.generate(this.product)
                // make sure all extra countries are defined
                if (this.product.extras) {
                  for (let i = 0; i < this.product.extras.length; i++) {
                    if (!this.product.extras[i].hasOwnProperty('pricing')) {
                      this.product.extras[i].pricing = []
                    }
                    if (!this.product.extras[i].hasOwnProperty('pricingInSSP')) {
                      this.product.extras[i].pricingInSSP = []
                    }
                    for (let c = 0; c < this.countries.length; c++) {
                      if (!this.product.extras[i].pricing.hasOwnProperty(this.countries[c].label)) {
                        this.product.extras[i].pricing[this.countries[c].label] = 0
                      }
                      if (!this.product.extras[i].pricingInSSP.hasOwnProperty(this.countries[c].label)) {
                        this.product.extras[i].pricingInSSP[this.countries[c].label] = 0
                      }
                    }
                  }
                }
              }
            }, {
              deep: true
            })
          }
        },
        families: {
          source: this.fb.database().ref('families').orderByChild('name')
        },
        countries: {
          source: this.fb.database().ref('countries')
        },
        extras: {
          source: this.fb.database().ref('extras')
        },
        duplicateJobs: {
          source: this.fb.database().ref('duplicateJobs')
        },
        productTypes: {
          source: this.fb.database().ref('productTypes').orderByChild('name'),
          asObject: true
        },
        roofs: {
          source: this.fb.database().ref('roofs').orderByChild('name'),
          asObject: true
        },
        colours: {
          source: this.fb.database().ref('colours').orderByChild('name'),
          asObject: true
        }
      }
    },
    computed: {
      productFamilyLabels () {
        return this.families.map((val) => {
          return {
            value: val['.key'],
            text: val.name
          }
        })
      }
    },
    methods: {
      back () {
        this.$router.push(ProductSearch)
      },
      getRoofOptions () {
        let roofs = []
        for (let key in this.roofs) {
          if (this.roofs.hasOwnProperty(key)) {
            if (this.roofs[key].name) {
              roofs.push({
                value: {
                  prefix: this.roofs[key].prefix,
                  label: this.roofs[key].name
                },
                text: this.roofs[key].name
              })
            }
          }
        }
        return roofs
      },
      getColourOptions () {
        let colours = []
        for (let key in this.colours) {
          if (this.colours.hasOwnProperty(key)) {
            if (this.colours[key].name) {
              colours.push({
                value: {
                  prefix: this.colours[key].prefix,
                  label: this.colours[key].name
                },
                text: this.colours[key].name
              })
            }
          }
        }
        return colours
      },
      getProductTypes () {
        let productTypes = []
        for (let key in this.productTypes) {
          if (this.productTypes.hasOwnProperty(key) && key !== '.key') {
            productTypes.push({
              value: this.productTypes[key],
              text: this.productTypes[key]
            })
          }
        }
        return productTypes
      },
      updateFamily () {
        console.log('updating family')
        let query = this.fb.database().ref('families/' + this.product.family)
        let self = this
        query.on('value', (val) => {
          console.log('product = ', self.product)
          self.product.name = [val.val().name, this.product.variation].join(' ')
          self.product._family = val.val()
          self.product._family['.key'] = this.product.family
          console.log('updated family')
        })
      },
      productExists (code) {
        return new Promise((resolve, reject) => {
          let exists = false
          this.fb.database().ref('products').orderByChild('code').equalTo(code).on('value', (data) => {
            data.forEach((obj) => {
              if (obj.val().code === code) {
                exists = true
              }
            })
          })
          resolve(exists)
        })
      },
      productFamilyOptions () {
        let query = this.fb.database().ref('families').orderByChild('name')
        query.on('value', (snapshot) => {
          snapshot.forEach((childSnapshot) => {
            console.log(childSnapshot.val())
          })
        })

        return []
      },
      calculatePrice (countryLabel) {
        // we need to get the base price
        let basePrice = parseFloat(this.product.ssp)
        if (isNaN(basePrice)) {
          basePrice = 0
        }
        if (this.product.extras) {
          this.product.extras.forEach((extra) => {
            if (extra.pricing.hasOwnProperty(countryLabel) && extra.pricingInSSP.hasOwnProperty(countryLabel) && extra.pricingInSSP[countryLabel] === '1') {
              basePrice += parseFloat(extra.pricing[countryLabel])
            }
          })
        }
        this.countries.forEach((country) => {
          if (country.label === countryLabel && country.hasOwnProperty('uplift') && country.uplift) {
            basePrice = basePrice + (basePrice * parseFloat(country.uplift))
          }
        })
        this.product.internationalPricing[countryLabel] = basePrice
        this.$forceUpdate()
      },
      calculateDimensions (propertyFrom, propertyFromUnits, propertyTo) {
        let value
        if (propertyFromUnits === 'in') {
          value = this.product[propertyFrom] / 0.39370
        } else {
          value = this.product[propertyFrom] * 0.39370
        }

        this.product[propertyTo] = value.toFixed(1)
        this.$forceUpdate()
      },
      updateProductCode (product) {

      },
      generateProductCode (product) {

      },
      addProductToDuplicateForm () {
        let newProduct = JSON.parse(JSON.stringify(this.product))
        if (this.duplicateForm.length > 0) {
          newProduct = JSON.parse(JSON.stringify(this.duplicateForm[this.duplicateForm.length - 1]))
        }
        delete newProduct._exists
        // newProduct.code = this.generateProductCode(newProduct)
        this.duplicateForm.push(newProduct)
      },
      removeProductFromDuplicateForm (index) {
        this.duplicateForm.splice(index, 1)
      },
      createDuplicates () {
        let newProduct
        let duplicateJob = {
          createdCodes: [],
          createdKeys: [],
          timestamp: (new Date().getUTCDate())
        }
        this.duplicating = true
        this.duplicatesCreated = 0
        for (let i = 0; i < this.duplicateForm.length; i++) {
          newProduct = this.duplicateForm[i]
          delete newProduct['.key']
          delete newProduct._family['.key']
          newProduct['published'] = false
          newProduct['lastModified'] = ServerValue.TIMESTAMP
          newProduct['name'] = [newProduct._family.name, newProduct.variation].join(' ')
          this.fb.database().ref('products').child(newProduct.code).set(newProduct)
          try {
            duplicateJob.createdCodes.push(newProduct['code'])
            duplicateJob.createdKeys.push(newProduct['code'])
          } catch (e) {
            console.log('Cannot create', newProduct)
          }
          this.duplicatesCreated++
        }
        let duplicateJobPush = this.fb.database().ref('duplicateJobs').push(duplicateJob)
        this.duplicating = false
        this.$refs.duplicateModal.hide()
        this.$refs.simplert.openSimplert({
          title: 'Complete',
          type: 'info',
          message: this.duplicateForm.length + ' new products have been added',
          customCloseBtnText: 'View Duplicates',
          onClose: () => {
            this.$router.push('/duplicateJobs/' + duplicateJobPush.key)
          }
        })
        this.duplicateForm = []
      },
      duplicate () {
        this.$refs.simplert.openSimplert({
          title: 'Duplicate',
          type: 'warning',
          message: 'Are you sure you want to duplicate this product?',
          useConfirmBtn: true,
          customConfirmBtnText: 'Yes, duplicate',
          customCloseBtnText: 'Nevermind',
          onConfirm: () => {
            delete this.product['.key']
            this.product.code += ' (copy)'
            this.product.name += ' (copy)'
            this.product.lastModified = ServerValue.TIMESTAMP
            let push = this.fb.database().ref('products').push(this.product)
            this.$router.push('/product/' + push.key)
          }
        })
      },
      publish () {
        this.$refs.simplert.openSimplert({
          title: 'Are you sure?',
          type: 'warning',
          message: 'Are you sure you want to publish this product to the website?',
          useConfirmBtn: true,
          onConfirm: () => {
            this.product.published = true
            delete this.product['.key']
            this.product.lastModified = ServerValue.TIMESTAMP
            this.$firebaseRefs.product.set(this.product).then(() => {
              this.$refs.simplert.openSimplert({
                title: 'Published',
                type: 'info',
                message: 'The product has been published to the website. Please allow up to 10 minutes for the product to appear.'
              })
            })
          }
        })
      },
      unpublish () {
        this.$refs.simplert.openSimplert({
          title: 'Are you sure?',
          type: 'warning',
          message: 'Are you sure you want to unpublish this product and remove it from the website?',
          useConfirmBtn: true,
          onConfirm: () => {
            this.product.published = false
            delete this.product['.key']
            delete this.product._family['.key']
            this.product.lastModified = ServerValue.TIMESTAMP
            this.$firebaseRefs.product.set(this.product).then(() => {
              this.$refs.simplert.openSimplert({
                title: 'Unpublished',
                type: 'info',
                message: 'The product has been unpublished from the website. Please allow up to 10 minutes for the product to be removed.'
              })
            })
          }
        })
      },
      del () {
        this.$refs.simplert.openSimplert({
          title: 'Are you sure?',
          type: 'warning',
          message: 'Are you sure you want to delete this product?',
          useConfirmBtn: true,
          onConfirm: () => {
            let self = this
            this.$firebaseRefs.product.remove().then(() => {
              self.back()
            })
          }
        })
      },
      save () {
        this.saving = true
        // make sure the product code is correct for this product
        this.product.code = this.$productCode.generate(this.product)
        this.product.lastModified = ServerValue.TIMESTAMP
        // get the key and the code and see if it needs updating
        let key = this.product['.key']
        let productCopy = this.product
        let code = this.product.code
        console.log('key = ', key)
        console.log('code = ', code)
        if (key !== code) {
          console.log('key and code do not match, I need to update the key')
          console.log('productCopy = ', productCopy)
          delete productCopy['.key']
          delete productCopy._family['.key']
          this.fb.database().ref('products').child(code).set(productCopy).then(() => {
            console.log('new product added, deleting old product')
            this.$firebaseRefs.product.remove().then(() => {
              console.log('done, redirecting')
              this.$router.push('/product/' + code)
              this.$refs.simplert.openSimplert({
                title: 'Saved',
                type: 'info',
                message: 'Saved',
                onClose: () => {
                  window.location.reload()
                }
              })
            })
          })
        } else {
          console.log('key and code match, updating current product')
          delete this.product['.key']
          delete this.product._family['.key']
          this.$firebaseRefs.product.set(this.product).then(() => {
            this.saving = false
            this.$refs.simplert.openSimplert({
              title: 'Saved',
              type: 'info',
              message: 'Saved'
            })
          })
        }
      }
    }
  }
</script>

<style>
    .product-update {
        margin-bottom: 120px;
    }
    @media screen and (min-width: 1600px) {
        #duplicate-modal .modal-lg {
            max-width: 1400px !important;
        }
    }
</style>



// WEBPACK FOOTER //
// src/components/ProductUpdate.vue